import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/hooks";

import AssetsOverviewModal from "./AssetsOverviewModal";
import ChargesOverviewModal from "./ChargeOverviewModal";
import SelectDropdownV2 from "../../../../src/components/ui/selectdropdown/v2";
import { selectAssetCount } from "../Logic/assetsSlice";
import OverviewModalButton from "../../../components/ui/button/v2/OverviewModalButton";
import { selectChargeCount } from "../Logic/chargesSlice";
import AddAssetModal from "./AddAssetModal";
import AddChargeModal from "./AddChargeModal";
import ClickMenuDropdown from "../../../components/ui/selectdropdown/v2/ClickMenuDropdown";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";

interface LegalTechnicalAnalysisHeaderProps {
  selectedLoanReferenceId?: string;
  onLoanSelectionChange: (loanReferenceId: string) => void;
}

type ActiveModalType =
  | "ASSETS_OVERVIEW"
  | "CHARGES_OVERVIEW"
  | "ADD_ASSET"
  | "ADD_CHARGE"
  | null;

const LegalTechnicalAnalysisHeader: React.FC<
  LegalTechnicalAnalysisHeaderProps
> = ({
  selectedLoanReferenceId,
  onLoanSelectionChange,
}) => {
  const loanAccounts = useAppSelector(getAccountProductInfo);

  const assetCount = useAppSelector(selectAssetCount);
  const chargeCount = useAppSelector(selectChargeCount);

  const [activeModal, setActiveModal] = useState<ActiveModalType>(null);

  const closeModal = () => setActiveModal(null);

  useEffect(() => {
    if (!selectedLoanReferenceId && loanAccounts.length >0) {
      onLoanSelectionChange(loanAccounts[0].loan_reference_id);
    }
  }, [loanAccounts,selectedLoanReferenceId,onLoanSelectionChange]);  

  const dropdownButtons = [
    {
      label: "New Asset",
      onClick: () => setActiveModal("ADD_ASSET"),
    },
    {
      label: "New Charge",
      onClick: () => setActiveModal("ADD_CHARGE"),
    },
  ];

  const loanOptions =
    loanAccounts.map((loan) => {
      const accountName = loan.account_name?.trim();

      return {
        name: loan.ext_account_number,
        value: loan.loan_reference_id,

        display_component: (
          <div className="flex w-full flex-col items-start justify-center gap-0.5">
            <span
              className="w-full truncate text-sm font-normal leading-5 text-[#0A0A0A]"
              title={loan.ext_account_number}
            >
              {loan.ext_account_number}
            </span>

            <span className="w-full truncate text-xs font-normal leading-4 text-[#737373]">
              {accountName || "Name unavailable"}
            </span>
          </div>
        ),

        selected_display_component: (
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-xs font-normal leading-4 text-[#737373]">
              Loan Account
            </span>

            <span
              className="truncate text-sm font-medium leading-none text-[#0A0A0A]"
              title={loan.ext_account_number}
            >
              {loan.ext_account_number}
            </span>
          </div>
        ),
      };
    });

  const selectedLoan =loanOptions.find((option) =>option.value ===selectedLoanReferenceId);

  return (
    <>
      <div
        className="
          flex items-center justify-between
          bg-[#E5E7EB]
          p-3
        "
      >
        <div className="w-[300px]">
          <SelectDropdownV2
            name="Loan Account"
            options={loanOptions}
            selectedOption={selectedLoan}
            onSelectionChange={(option) =>
              onLoanSelectionChange(option.value)
            }
            enableSearch
            optionClassName="px-2 py-1.5"
            selectedContainerClassName="rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
          />
        </div>

        <div className="flex items-center gap-3">
          <OverviewModalButton
            label="Assets"
            count={assetCount}
            onClick={() => setActiveModal("ASSETS_OVERVIEW")}
          />

          <OverviewModalButton
            label="Charges"
            count={chargeCount}
            onClick={() => setActiveModal("CHARGES_OVERVIEW")}
          />

          <ClickMenuDropdown
            dropdownLabel="Add"
            buttons={dropdownButtons}
          />
        </div>
      </div>

      {activeModal === "ASSETS_OVERVIEW" && (
        <AssetsOverviewModal onClose={closeModal} />
      )}

      {activeModal === "CHARGES_OVERVIEW" && (
        <ChargesOverviewModal onClose={closeModal} />
      )}

      {activeModal === "ADD_ASSET" && (
        <AddAssetModal
          onClose={closeModal}
          onBack={closeModal}
        />
      )}

      {activeModal === "ADD_CHARGE" && (
        <AddChargeModal
          onClose={closeModal}
          onBack={closeModal}
        />
      )}
    </>
  );
};

export default LegalTechnicalAnalysisHeader;